import axios from 'axios';
import { ApiResponse, Pool } from '../types/api';
import { API_ORIGIN } from '../config';

// API 설정
const API_URL = API_ORIGIN;

// 캐시 맵 정의 - 성능 최적화를 위한 메모리 캐시
const poolCodeToIdCache = new Map<string, string>();
const poolIdToCodeCache = new Map<string, string>();

/**
 * 코드-ID 매핑 캐시 초기화 함수
 * 특정 코드나 ID에 대한 캐시만 지우거나, 모든 캐시를 초기화할 수 있습니다.
 */
export const clearPoolCache = (poolCode?: string, poolId?: string): void => {
  // 특정 코드와 ID에 대한 캐시 초기화
  if (poolCode && poolId) {
    poolCodeToIdCache.delete(poolCode);
    poolIdToCodeCache.delete(poolId);
    console.log(`[poolUtils] 시설 캐시 초기화: 코드(${poolCode}), ID(${poolId})`);
    return;
  }

  // 특정 코드만 해당하는 캐시 초기화
  if (poolCode) {
    poolCodeToIdCache.delete(poolCode);
    console.log(`[poolUtils] 시설 코드 캐시 초기화: ${poolCode}`);
    return;
  }

  // 특정 ID만 해당하는 캐시 초기화
  if (poolId) {
    poolIdToCodeCache.delete(poolId);
    console.log(`[poolUtils] 시설 ID 캐시 초기화: ${poolId}`);
    return;
  }

  // 모든 캐시 초기화
  poolCodeToIdCache.clear();
  poolIdToCodeCache.clear();
  console.log('[poolUtils] 모든 시설 캐시 초기화');
};

/**
 * 풀 코드를 사용하여 풀 ID를 조회하는 함수
 * @param poolCode 풀 코드
 * @returns 조회된 풀 ID 또는 null
 */
export const getPoolIdByCode = async (poolCode: string): Promise<number | null> => {
    try {
        console.log('[poolUtils] 풀 코드', poolCode, '로 풀 ID 조회 시작');
        
        // 캐시 확인
        const cachedId = poolCodeToIdCache.get(poolCode);
        if (cachedId) {
            console.log(`[poolUtils] 캐시에서 풀 ID 찾음: ${cachedId}`);
            return Number(cachedId);
        }
        
        const response = await axios.get(`${API_URL}/api/pools/code/${poolCode}`);
        if (response.data && response.data.id) {
            const poolId = response.data.id;
            console.log(`[poolUtils] 풀 코드 ${poolCode}에 해당하는 풀 ID: ${poolId}`);
            
            // 캐시에 저장
            poolCodeToIdCache.set(poolCode, String(poolId));
            poolIdToCodeCache.set(String(poolId), poolCode);
            
            return poolId;
        }
        return null;
    } catch (error) {
        console.error('[poolUtils] 풀 코드', poolCode, '로 풀 ID 조회 실패:', error);
        return null;
    }
};

/**
 * 풀 ID로 풀 코드를 조회하는 함수
 * @param poolId 풀 ID
 * @returns 풀 코드 또는 에러 정보가 담긴 응답
 */
export async function getPoolCodeById(poolId: number): Promise<ApiResponse<string>> {
  try {
    console.log(`풀 ID ${poolId}로 풀 코드 조회 시작`);
    
    // 캐시 확인
    const cachedCode = poolIdToCodeCache.get(String(poolId));
    if (cachedCode) {
      console.log(`캐시에서 풀 코드 찾음: ${cachedCode}`);
      return {
        success: true,
        data: cachedCode,
        message: '풀 코드 조회 성공 (캐시)',
        status: 200
      };
    }
    
    const response = await axios.get(`${API_URL}/api/pools/${poolId}`);
    
    if (response.data && response.data.code) {
      const poolCode = response.data.code;
      console.log(`풀 ID ${poolId}에 해당하는 풀 코드: ${poolCode}`);
      
      // 캐시에 저장
      poolIdToCodeCache.set(String(poolId), poolCode);
      poolCodeToIdCache.set(poolCode, String(poolId));
      
      return {
        success: true,
        data: poolCode,
        message: '풀 코드 조회 성공',
        status: 200
      };
    }
    
    return {
      success: false,
      data: null,
      message: `풀 ID ${poolId}에 해당하는 풀을 찾기 불가`,
      status: 404
    };
  } catch (error) {
    console.error(`풀 ID ${poolId}로 풀 코드 조회 실패:`, error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || '풀 코드 조회 중 오류 발생',
      status: error.response?.status || 500
    };
  }
}

/**
 * 시설 코드로 시설이 존재하는지 확인하는 함수
 * @param poolCode 시설 코드
 * @returns 존재 여부 (boolean)
 */
export const isPoolExists = async (poolCode: string): Promise<boolean> => {
  const poolId = await getPoolIdByCode(poolCode);
  return poolId !== null;
};

/** 통합 관제·대시보드에서 “정상(운영)”으로 취급하는 시설 상태 */
const NORMAL_POOL_STATUS_VALUES = new Set(['active', 'open', 'operating']);

/**
 * 시설 운영 상태가 정상(운영 중)인지 여부
 */
export const isNormalOperatingPoolStatus = (
  status: string | undefined,
  isActive?: boolean,
): boolean => {
  if (isActive === false) return false;
  if (!status) return false;
  const normalized = status.trim().toLowerCase();
  if (
    normalized === 'inactive' ||
    normalized === 'closed' ||
    normalized === 'maintenance' ||
    normalized === 'alert' ||
    normalized === 'error'
  ) {
    return false;
  }
  return NORMAL_POOL_STATUS_VALUES.has(normalized);
};

/**
 * 시설 상태 텍스트를 기반으로 상태 색상 반환
 * @param status 시설 상태 텍스트
 * @returns 상태에 해당하는 색상 코드
 */
export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
    case '활성':
      return '#4caf50'; // 녹색
    case 'inactive':
    case '비활성':
      return '#f44336'; // 빨간색
    case 'maintenance':
    case '유지보수':
      return '#ff9800'; // 주황색
    case 'warning':
    case '경고':
      return '#ffeb3b'; // 노란색
    default:
      return '#9e9e9e'; // 회색 (기본값)
  }
};

/**
 * 시설 타입에 따른 아이콘 클래스명 반환
 * @param type 시설 타입
 * @returns 아이콘 클래스명
 */
export const getPoolTypeIcon = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'indoor':
    case '실내':
      return 'fa-building';
    case 'outdoor':
    case '야외':
      return 'fa-sun';
    case 'olympic':
    case '올림픽':
      return 'fa-medal';
    case 'children':
    case '어린이':
      return 'fa-child';
    default:
      return 'fa-water';
  }
};

/**
 * 시설 코드 생성 - 'POOL' 접두사와 3자리 숫자
 * @param lastCode 마지막으로 생성된 코드 (없을 경우 'POOL000')
 * @returns 새로운 시설 코드
 */
export const generatePoolCode = (lastCode: string = 'POOL000'): string => {
  const prefix = 'POOL';
  const currentNumber = parseInt(lastCode.replace(prefix, ''), 10);
  const nextNumber = currentNumber + 1;
  return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
};

/**
 * 시설 주소 포맷팅
 * @param pool 시설 객체
 * @returns 포맷팅된 주소 문자열
 */
export const formatPoolAddress = (pool: Pool): string => {
  if (!pool) return '';
  
  const address = [];
  if (pool.address) address.push(pool.address);
  if (pool.location) address.push(pool.location);
  
  return address.join(', ');
};

// API 서비스에서 사용할 풀 코드 <-> 풀 ID 변환 유틸리티 함수
export const poolUtils = {
  /**
   * 풀 코드를 사용하여 풀 ID를 조회합니다.
   * @param poolCode 변환할 풀 코드
   * @returns 풀 ID 또는 조회 실패 시 에러 정보를 포함한 응답
   */
  getPoolIdByCode: async (poolCode: string): Promise<ApiResponse<string>> => {
    try {
      console.log(`풀 코드 ${poolCode}에 해당하는 ID 조회 중...`);
      const response = await axios.get(`${API_URL}/api/pools/code/${poolCode}`);
      
      if (response.data && response.data.id) {
        console.log(`풀 코드 ${poolCode}의 ID 조회 성공: ${response.data.id}`);
        return {
          success: true,
          data: String(response.data.id),
          message: '풀 ID 조회 성공',
          status: response.status
        };
      } else {
        return {
          success: false,
          data: null,
          message: '풀 정보를 찾기 불가',
          status: response.status
        };
      }
    } catch (error) {
      console.error(`풀 코드 ${poolCode}의 ID 조회 실패:`, error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || `풀 코드 ${poolCode}에 해당하는 ID를 찾기 불가`,
        status: error.response?.status || 500
      };
    }
  },

  /**
   * 풀 ID를 사용하여 풀 코드를 조회합니다.
   * @param poolId 변환할 풀 ID
   * @returns 풀 코드 또는 조회 실패 시 에러 정보를 포함한 응답
   */
  getPoolCodeById: async (poolId: string | number): Promise<ApiResponse<string>> => {
    try {
      console.log(`풀 ID ${poolId}에 해당하는 코드 조회 중...`);
      const response = await axios.get(`${API_URL}/api/pools/${poolId}`);
      
      if (response.data && response.data.code) {
        console.log(`풀 ID ${poolId}의 코드 조회 성공: ${response.data.code}`);
        return {
          success: true,
          data: response.data.code,
          message: '풀 코드 조회 성공',
          status: response.status
        };
      } else {
        return {
          success: false,
          data: null,
          message: '풀 정보를 찾기 불가',
          status: response.status
        };
      }
    } catch (error) {
      console.error(`풀 ID ${poolId}의 코드 조회 실패:`, error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || '풀 코드 조회 중 오류 발생',
        status: error.response?.status || 500
      };
    }
  }
};